import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useValidation from '../../../../../components/CustomFormsComponents/hooks/useValidation';
import {
    RoamSiniestroRaw,
    RoamSrtParametricData,
    RoamSrtParametricItem,
    useGetSiniestroByIdQuery,
    useGetSrtParametricDataQuery,
    useLazyGetSiniestrosQuery
} from '../../../../../services/roamApi';
import { useGetCIIUCodesQuery } from '../../../../../services/companyApi';
import {
    useGetContractsByOrganizacionQuery,
    useGetContractsQuery
} from '../../../../../services/contratctsApi';
import { useGetOrganizationsQuery } from '../../../../../services/organizationsApi';
import { GRV } from '../../../../../constants/values';
import { mapRoamSiniestro } from '../../../adapters/roam';
import { buildConstructionSiteFields, buildSrtFormSections } from '../config/srtFormConfig';
import {
    SrtBaseFieldConfig,
    SrtDividerConfig,
    SrtFieldConfig,
    SrtFormErrors,
    SrtFormValues,
    SrtRepeatableConfig,
    SrtSectionConfig
} from '../types';
import { isSrtFieldVisible, resolveSrtField } from '../utils/srtFields';
import { syncInterviewDates } from '../utils/interviewedDates';
import {
    applyEmployerDefaultsToWorkCenter,
    updateEmployerFieldWithWorkCenterDefault
} from '../utils/workCenterDefaults';

const isRepeatableField = (field: SrtFieldConfig): field is SrtRepeatableConfig =>
    field.type === 'repeatable';

const isDividerField = (field: SrtFieldConfig): field is SrtDividerConfig =>
    field.type === 'divider';

const createEmptyRepeatableRow = (field: SrtRepeatableConfig) => {
    const row = field.fields.reduce<Record<string, any>>((acc, childField) => {
        acc[childField.name] = childField.defaultValue ?? (childField.type === 'file' ? null : '');
        return acc;
    }, {});

    if (field.nestedRepeatable) {
        row[field.nestedRepeatable.name] = [createEmptyMedida()];
    }

    return row;
};

const createEmptyMedida = () => ({
    nroMedida: '',
    medidaCorrectiva: '',
    fechaInicio: '',
    fechaFin: ''
});

const hasMeasureContent = (medida: Record<string, any>) =>
    [medida?.medidaCorrectiva, medida?.fechaInicio, medida?.fechaFin].some(
        (value) => value !== null && value !== undefined && String(value).trim() !== ''
    );

// Cada causa puede tener varias medidas (boton "+" en la UI). Nro. de medida NO se
// autocompleta: el preventor lo tipea a mano (1, 2, ...) para cada medida nueva. Se sigue
// validando en validateField (groupName "medidasCorrectivas.<i>.medidas") que sea correlativo
// dentro de una unica secuencia GLOBAL a lo largo de todas las causas con
// requiereMedidaCorrectiva = si (1..maxOtraMedida+1).
const normalizeMedidasRows = (medidas: Record<string, any>[] | undefined) =>
    medidas && medidas.length > 0 ? medidas : [createEmptyMedida()];

export const normalizeMedidasCorrectivasRows = (rows: Record<string, any>[] | undefined) =>
    (rows || []).map((row, index) => {
        const nroCausa = String(index + 1);
        const medidas = normalizeMedidasRows(row.medidas);
        const hasExistingMeasure = medidas.some(hasMeasureContent);
        const requiereMedidaCorrectiva =
            row.requiereMedidaCorrectiva === 'si'
                ? 'si'
                : row.requiereMedidaCorrectiva === 'no'
                  ? 'no'
                  : hasExistingMeasure
                    ? 'si'
                    : 'no';

        return {
            ...row,
            nroCausa,
            requiereMedidaCorrectiva,
            medidas
        };
    });

const toInputDateValue = (value: any) => {
    if (!value) return '';
    if (typeof value === 'string') return value.includes('T') ? value.split('T')[0] : value;

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().split('T')[0];
};

const toInputTimeValue = (value: any) => {
    if (!value) return '';
    if (typeof value === 'string') {
        if (value.includes('T')) return value.split('T')[1]?.slice(0, 5) || '';
        return value.slice(0, 5);
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().slice(11, 16);
};

export const resolveOtherAffectedAutoFill = (matchedSiniestros: RoamSiniestroRaw[] | undefined) => {
    const matches = matchedSiniestros || [];

    const nroDenuncia = Array.from(
        new Set(
            matches
                .map((item) => mapRoamSiniestro(item).nroRoam)
                .map((value) => String(value || '').trim())
                .filter(Boolean)
        )
    ).join(', ');

    const patologiaTrazadora =
        matches
            .map((item) => mapRoamSiniestro(item).patologiasTrazadoras)
            .find((value): value is string => Boolean(value)) || '';

    const apellidoNombres =
        matches
            .map((item) =>
                [item?.accidentado?.apellido, item?.accidentado?.nombre]
                    .map((value) => String(value || '').trim())
                    .filter(Boolean)
                    .join(' ')
            )
            .find((value) => Boolean(value)) || '';

    return { nroDenuncia, patologiaTrazadora, apellidoNombres };
};

export const buildInitialValues = (siniestro?: any): SrtFormValues => {
    const values = buildSrtFormSections(((key: string) => key) as any).reduce<SrtFormValues>(
        (acc, section) => {
            section.fields.forEach((field) => {
                if (isDividerField(field)) return;

                if (isRepeatableField(field)) {
                    const initialRows = [createEmptyRepeatableRow(field)];
                    acc[field.name] =
                        field.name === 'medidasCorrectivas'
                            ? normalizeMedidasCorrectivasRows(initialRows)
                            : initialRows;
                    return;
                }

                acc[field.name] = field.type === 'file' ? null : '';
            });

            return acc;
        },
        {}
    );

    const accidentado = siniestro?.accidentado || {};
    const trabajadorLocalidad = accidentado?.datosContacto?.localidad || {};
    const trabajadorProvincia = trabajadorLocalidad?.provincia || {};

    return {
        ...values,
        nroSiniestroArt: siniestro?.nroSiniestro || '',
        nroDenuncia: getRoamNumber(siniestro),
        apellidoTrabajador: accidentado?.apellido || '',
        nombresTrabajador: accidentado?.nombre || '',
        cuilTrabajador: accidentado?.cuil || '',
        fechaAccidente: toInputDateValue(siniestro?.fechaOcurrrencia),
        fechaDatosAccidente: toInputDateValue(siniestro?.fechaOcurrrencia),
        horaAccidente: toInputTimeValue(siniestro?.horaOcurrencia || siniestro?.fechaOcurrrencia),
        descripcion: siniestro?.relatoHecho || '',
        fechaNacimiento: toInputDateValue(accidentado?.fechaNacimiento),
        domicilioTrabajador: accidentado?.datosContacto?.direccion || '',
        ciudadTrabajador: trabajadorLocalidad?.nombre || '',
        provinciaTrabajador: trabajadorProvincia?.nombre || '',
        codigoPostalTrabajador: trabajadorLocalidad?.codigoPostal
            ? String(trabajadorLocalidad.codigoPostal)
            : '',
        idLocalidadTrabajador:
            accidentado?.datosContacto?.idLocalidad || trabajadorLocalidad?.id || '',
        idProvinciaTrabajador: trabajadorProvincia?.id || '',
        telefonoTrabajador:
            accidentado?.datosContacto?.telefonoCelular ||
            accidentado?.datosContacto?.telefonoPrincipal ||
            '',
        razonSocialEmpleador:
            siniestro?.datosSimp?.organismo?.organismoCentral?.nombre ||
            siniestro?.datosSimp?.establecimiento?.organismo?.organismoCentral?.nombre ||
            siniestro?.empleadorRazonSocial ||
            '',
        cuitDatosEmpleador:
            siniestro?.datosSimp?.organismo?.organismoCentral?.cuit ||
            siniestro?.datosSimp?.establecimiento?.organismo?.organismoCentral?.cuit ||
            siniestro?.cuitEmpleador ||
            '',
        domicilioLugar: siniestro?.lugarOcurrencia?.calle || '',
        numeroLugar: siniestro?.lugarOcurrencia?.altura || '',
        cpaLugar: siniestro?.lugarOcurrencia?.codigoPostal || '',
        // CPA no tiene un dato equivalente en "Lugar de ocurrencia" (Información del siniestro),
        // así que queda siempre en blanco para completarse a mano (AC Escenario-03).
        lugarCPA: '',
        ciudadLugar: siniestro?.lugarOcurrencia?.localidad?.nombre || '',
        provinciaLugar: siniestro?.lugarOcurrencia?.localidad?.provincia?.nombre || '',
        // La descripción del accidente la redacta el Preventor: no se precarga con el relato de
        // la denuncia, que ya está en `descripcion` (SMP-485). Un formulario guardado conserva lo
        // suyo porque resetForm pone lo guardado por encima de estos valores.
        descripcionAccidente: ''
    };
};

export const hasValue = (value: any) => {
    if (Array.isArray(value)) return value.some((item) => Object.values(item || {}).some(hasValue));
    if (value instanceof File) return true;
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return true;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
};

const buildEmployerDomicileFromContract = (contractData?: any) =>
    [contractData?.direccion?.calle, contractData?.direccion?.altura]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .join(' ');

// Autocompleta Teléfono, Domicilio y Código CIIU del empleador con los datos del contrato
// (organizacion_central), solo mientras el campo esté vacío. Una vez que el Formulario SRT
// se guarda, los valores reales persistidos llegan vía `mapSrtFormularioToValues`/`resetForm`
// y toman prioridad porque ya no están en blanco.
//
// `ciiuRevision3.id` es la FK directa al catálogo `codigos-ciiu` que arma las opciones del combo
// (ver `useGetCIIUCodesQuery`): el id de ambos lados se corresponde 1 a 1. El listado de
// organismos-centrales filtrado por CUIT (`contractData`) no trae `ciiuRevision3`, así que viene
// de una segunda consulta al mismo endpoint filtrada por `idOrganizacion` (ver
// `employerOrganizationContractsList` en `useFormSRT`).
//
// Código CIIU del "Centro de Trabajo" (sección 04) usa el mismo `ciiuRevision3Id`: `cuitCentro`
// es siempre el mismo CUIT que `cuitDatosEmpleador` (ver `workCenterDefaults.ts`), así que es la
// misma organización y no hace falta una consulta aparte.
export const applyEmployerContractDefaults = (
    values: SrtFormValues,
    contractData?: any,
    ciiuRevision3Id?: number | string | null
): SrtFormValues => {
    if (!contractData) return values;

    return {
        ...values,
        telefonoEmpleador: hasValue(values.telefonoEmpleador)
            ? values.telefonoEmpleador
            : contractData?.telefono || '',
        domicilioEmpleador: hasValue(values.domicilioEmpleador)
            ? values.domicilioEmpleador
            : buildEmployerDomicileFromContract(contractData),
        codigoCiiuEmpleador: hasValue(values.codigoCiiuEmpleador)
            ? values.codigoCiiuEmpleador
            : ciiuRevision3Id
              ? String(ciiuRevision3Id)
              : '',
        codigoCiiuCentro: hasValue(values.codigoCiiuCentro)
            ? values.codigoCiiuCentro
            : ciiuRevision3Id
              ? String(ciiuRevision3Id)
              : ''
    };
};

const shouldCountFieldForSectionCompletion = (
    sectionId: number,
    field: SrtFieldConfig,
    values: SrtFormValues
) => {
    if (isDividerField(field)) return false;

    if (isRepeatableField(field)) return true;

    if (sectionId === 1 && field.name === 'otrosNacionalidad') {
        return String(values.nacionalidad || '') === '7';
    }

    if (sectionId === 2 && SECTION_2_OPTIONAL_FIELDS.has(field.name)) return false;
    if (sectionId === 4 && SECTION_4_OPTIONAL_FIELDS.has(field.name)) return false;

    return true;
};

const hasSectionContent = (section: SrtSectionConfig, values: SrtFormValues) =>
    section.fields.some((field) => {
        if (isDividerField(field)) return false;
        if (!shouldCountFieldForSectionCompletion(section.id, field, values)) return false;

        if (isRepeatableField(field)) {
            const rows = values[field.name] || [];
            return rows.some((row: Record<string, any>) =>
                field.fields
                    .filter(
                        (childField) => !childField.readOnly && isSrtFieldVisible(childField, row)
                    )
                    .some((childField) => hasValue(row?.[childField.name]))
            );
        }

        return hasValue(values[field.name]);
    });

export const isSectionComplete = (section: SrtSectionConfig, values: SrtFormValues) => {
    if (!hasSectionContent(section, values)) return false;

    return section.fields.every((field) => {
        if (isDividerField(field)) return true;
        if (!shouldCountFieldForSectionCompletion(section.id, field, values)) return true;

        if (isRepeatableField(field)) {
            const rows = values[field.name] || [];
            const contentRows = rows.filter((row: Record<string, any>) =>
                field.fields
                    .filter(
                        (childField) => !childField.readOnly && isSrtFieldVisible(childField, row)
                    )
                    .some((childField) => hasValue(row?.[childField.name]))
            );

            // Sin esto, un repeatable required en 0 filas con contenido pasaba el every()
            // de mas abajo de forma vacua (array vacio -> every() = true), marcando la
            // seccion como completa aunque el repeatable no tenga ningun dato cargado.
            if (field.required && contentRows.length === 0) return false;

            return contentRows.every((row: Record<string, any>) =>
                field.fields
                    .filter((childField) => isSrtFieldVisible(childField, row))
                    .map((childField) => resolveSrtField(childField, row))
                    .every((childField) => {
                        if (childField.required && !hasValue(row?.[childField.name])) {
                            return false;
                        }

                        const fixedLength = childField.validations?.find(
                            (validation) => validation.name === 'fixedLength'
                        )?.param;

                        return (
                            !fixedLength ||
                            !hasValue(row?.[childField.name]) ||
                            String(row[childField.name]).length === fixedLength
                        );
                    })
            );
        }

        return !field.required || hasValue(values[field.name]);
    });
};

const getErrorKey = (fieldName: string, rowIndex?: number, childName?: string) =>
    rowIndex === undefined || !childName ? fieldName : `${fieldName}.${rowIndex}.${childName}`;

const CONSTRUCTION_SITE_VALUE = '7';
const MAX_REPEATABLE_ROWS = 10;
const MAX_FULL_NAME_LENGTH = 34;

const SECTION_2_OPTIONAL_FIELDS = new Set(['telefonoEmpleador']);
const SECTION_4_OPTIONAL_FIELDS = new Set([
    'telefonoLugar',
    'cuitCentro',
    'actividadEconomicaCentro',
    'codigoCiiuCentro',
    'denominacionArtCentro',
    'codigoArtCentro',
    'otrosFiscalizacion',
    'tipoEstablecimiento',
    'dotacionActualCentro'
]);

const getRoamNumber = (siniestro?: any) =>
    String(siniestro?.nroRoam || siniestro?.numeroRoam || siniestro?.roam || '').slice(0, 6);

const RESPONSIBLE_NAME_FIELDS = new Set(['responsableApellido', 'responsableNombres']);

const getResponsibleFullNameLength = (values: SrtFormValues) =>
    String(values.responsableApellido || '').length +
    String(values.responsableNombres || '').length;

const limitResponsibleNamePart = (name: string, value: any, currentValues: SrtFormValues) => {
    if (!RESPONSIBLE_NAME_FIELDS.has(name)) return value;

    const otherFieldName =
        name === 'responsableApellido' ? 'responsableNombres' : 'responsableApellido';
    const otherFieldLength = String(currentValues[otherFieldName] || '').length;
    const maxLength = Math.max(MAX_FULL_NAME_LENGTH - otherFieldLength, 0);

    return String(value || '').slice(0, maxLength);
};

const buildParametricOption = (item: RoamSrtParametricItem) => ({
    label:
        item.codigo || item.codigoAseguradora
            ? `${item.codigo || item.codigoAseguradora} - ${item.descripcion || item.nombre}`
            : item.descripcion || item.nombre || '',
    value: String(item.id),
    description: item.descripcion || item.nombre || ''
});

const normalizeText = (value: any) =>
    String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();

const findParametricItemByDescription = (
    items: RoamSrtParametricItem[] | undefined,
    description: any
) => {
    const normalizedDescription = normalizeText(description);

    if (!normalizedDescription) return null;

    return (
        (items || []).find((item) => normalizeText(item.descripcion) === normalizedDescription) ||
        (items || []).find((item) =>
            normalizeText(item.descripcion).includes(normalizedDescription)
        ) ||
        (items || []).find((item) =>
            normalizedDescription.includes(normalizeText(item.descripcion))
        ) ||
        null
    );
};

// La "Organización" (aseguradora/autoseguro) del contrato vive en un catálogo distinto
// (organismos/organizaciones, del módulo Contratos) al que usa el select `denominacionArtEa`
// (el catálogo paramétrico del SRT). Se cruzan por nombre normalizado para ubicar, dentro del
// catálogo del SRT, la opción que corresponde a la organización real del contrato.
const findOrganizacionArtEaMatch = (
    contractOrganizationName: string | undefined,
    srtOrganizaciones?: RoamSrtParametricItem[]
): RoamSrtParametricItem | null => {
    const normalizedName = normalizeText(contractOrganizationName);

    if (!normalizedName) return null;

    return (
        (srtOrganizaciones || [])
            // GRV no es una opción seleccionable del combo, tampoco debería autocompletarse.
            .filter((item) => item.nombre !== GRV)
            .find((item) => normalizeText(item.nombre || item.descripcion) === normalizedName) ||
        null
    );
};

// Autocompleta Denominación ART/EA y Código ART/EA con la organización del contrato,
// solo mientras el campo esté vacío (mismo criterio que applyEmployerContractDefaults).
const applyArtEaContractDefaults = (
    values: SrtFormValues,
    matchedOrganization?: RoamSrtParametricItem | null
): SrtFormValues => {
    if (!matchedOrganization) return values;

    return {
        ...values,
        denominacionArtEa: hasValue(values.denominacionArtEa)
            ? values.denominacionArtEa
            : String(matchedOrganization.id),
        codigoArtEa: hasValue(values.codigoArtEa)
            ? values.codigoArtEa
            : String(matchedOrganization.codigoAseguradora || matchedOrganization.codigo || '')
    };
};

const mapFieldOptions = (
    field: SrtFieldConfig,
    parametricData?: RoamSrtParametricData,
    ciiuCodes?: any[]
): SrtFieldConfig => {
    if (isDividerField(field)) return field;

    if (isRepeatableField(field)) {
        return {
            ...field,
            fields: field.fields.map(
                (childField) =>
                    mapFieldOptions(childField, parametricData, ciiuCodes) as SrtBaseFieldConfig
            ),
            ...(field.nestedRepeatable
                ? {
                      nestedRepeatable: {
                          ...field.nestedRepeatable,
                          fields: field.nestedRepeatable.fields.map(
                              (childField) =>
                                  mapFieldOptions(
                                      childField,
                                      parametricData,
                                      ciiuCodes
                                  ) as SrtBaseFieldConfig
                          )
                      }
                  }
                : {})
        };
    }

    if (!field.optionSource) return field;

    if (field.optionSource === 'ciiuCodes') {
        return {
            ...field,
            options: (ciiuCodes || []).map((item: any) => ({
                label: `${item.codigo} - ${item.descripcion}`,
                value: String(item.id)
            }))
        };
    }

    if (!parametricData) return field;

    const sourceItems = parametricData[field.optionSource] || [];
    // GRV es la propia aseguradora/backoffice de la plataforma, no una ART/EA seleccionable
    // por el preventor para un empleador (mismo criterio que ya aplica el módulo de Contratos).
    const visibleSourceItems =
        field.optionSource === 'organizaciones'
            ? sourceItems.filter((item) => item.nombre !== GRV)
            : sourceItems;

    return {
        ...field,
        options: visibleSourceItems.map(buildParametricOption)
    };
};

const mapBaseFieldOptions = (
    field: SrtBaseFieldConfig,
    parametricData?: RoamSrtParametricData,
    ciiuCodes?: any[]
): SrtBaseFieldConfig => mapFieldOptions(field, parametricData, ciiuCodes) as SrtBaseFieldConfig;

const getSelectedParametricItem = (
    fieldName: string,
    value: any,
    parametricData?: RoamSrtParametricData
) => {
    const fieldSourceMap: Partial<Record<string, keyof RoamSrtParametricData>> = {
        grupoFiscalizacion: 'gruposFiscalizacion',
        grupoFiscalizacionCentro: 'gruposFiscalizacion',
        nacionalidad: 'nacionalidades',
        tipoLugar: 'tiposLugares',
        denominacionArtEa: 'organizaciones',
        codigoZonaCuerpo: 'zonasCuerpo',
        codigoNaturalezaLesion: 'naturalezaLesiones',
        codigoFormaAccidente: 'formasAccidentes',
        codigoAgenteCausante: 'agentesCausantes'
    };
    const sourceKey = fieldSourceMap[fieldName];

    if (!sourceKey || value === null || value === undefined || value === '') return null;

    return (parametricData?.[sourceKey] || []).find((item) => String(item.id) === String(value));
};

export const useFormSRT = (siniestro?: any, externalParametricData?: any) => {
    const { t: tValidation } = useTranslation('validations');
    const { t } = useTranslation('siniestrosRoam');
    const { validate } = useValidation(tValidation);
    const { data: fetchedParametricData } = useGetSrtParametricDataQuery(undefined, {
        skip: Boolean(externalParametricData)
    });
    const srtParametricData = externalParametricData || fetchedParametricData;
    const { data: ciiuCodesResponse } = useGetCIIUCodesQuery(null);
    const hasOrganismoCentralFromSiniestro = Boolean(
        siniestro?.datosSimp?.organismo?.organismoCentral ||
        siniestro?.datosSimp?.establecimiento?.organismo?.organismoCentral
    );
    // El flujo de Visitas (Preventor) no arma `datosSimp` (esa forma es propia de la ficha de
    // ROAM/Siniestros, ver `Detail.tsx`); y el `cuitEmpleador` suelto que trae la denuncia de la
    // visita (`getVisitaSiniestro`) puede venir en null, así que no es una fuente confiable. Como
    // sí tenemos el id del siniestro de ROAM (`siniestro.id`, ver `buildSiniestroForForm` en
    // FormularioSRTPage), se pide ese mismo siniestro por el endpoint que usa la ficha de
    // Siniestros para sacar el CUIT/id del organismo central real del empleador. Se salta en el
    // flujo ROAM porque ahí `datosSimp` ya viene en el `siniestro` recibido.
    const { data: roamSiniestroForEmployer } = useGetSiniestroByIdQuery(Number(siniestro?.id), {
        skip: hasOrganismoCentralFromSiniestro || !siniestro?.id
    });
    const employerOrganismoCentral =
        roamSiniestroForEmployer?.datosSimp?.organismo?.organismoCentral ||
        roamSiniestroForEmployer?.datosSimp?.establecimiento?.organismo?.organismoCentral;
    const organismoCentralId =
        siniestro?.datosSimp?.organismo?.organismoCentral?.id ||
        siniestro?.datosSimp?.establecimiento?.organismo?.organismoCentral?.id ||
        employerOrganismoCentral?.id ||
        null;
    const organismoCentralCuit =
        siniestro?.datosSimp?.organismo?.organismoCentral?.cuit ||
        siniestro?.datosSimp?.establecimiento?.organismo?.organismoCentral?.cuit ||
        employerOrganismoCentral?.cuit ||
        siniestro?.cuitEmpleador ||
        null;
    // No existe un filtro por id en este endpoint de listado, así que se busca por CUIT
    // (el mismo que usa la pantalla de Contratos para traer domicilio/localidad/CP del contrato,
    // a diferencia de organismo-central/{id} que no incluye "direccion").
    const { data: employerContractsResponse } = useGetContractsQuery(
        { cuit: organismoCentralCuit, skip: 0, take: 10 },
        { skip: !organismoCentralCuit }
    );
    const employerContractsList = employerContractsResponse?.data?.data || [];
    const employerContractData =
        employerContractsList.find(
            (contract: any) => Number(contract?.id) === Number(organismoCentralId)
        ) ||
        employerContractsList.find(
            (contract: any) => String(contract?.cuit || '') === String(organismoCentralCuit || '')
        ) ||
        employerContractsList[0];
    // El listado de organismos-centrales filtrado por CUIT (arriba) no trae ciiuRevision3, así que
    // hace falta esta consulta aparte (organismos/organismos-central, no organismos-centrales),
    // filtrada por idOrganizacion (la aseguradora/autoseguro del contrato), para poder
    // autocompletar el Código CIIU del empleador (sección 02).
    const { data: employerOrganizationContractsResponse } = useGetContractsByOrganizacionQuery(
        { idOrganizacion: employerContractData?.idOrganizacion, skip: 0, take: 1 },
        { skip: !employerContractData?.idOrganizacion }
    );
    const employerOrganizationContractsList =
        employerOrganizationContractsResponse?.data?.organismosCentral || [];
    // Cuando `organismoCentralId` no viene en el siniestro (flujo Visitas, ver arriba), se usa
    // el id del contrato ya resuelto por CUIT (`employerContractData`), que es el mismo id de
    // organismo central.
    const employerCiiuRevision3Id =
        employerOrganizationContractsList.find(
            (contract: any) =>
                Number(contract?.id) === Number(organismoCentralId || employerContractData?.id)
        )?.ciiuRevision3?.id || null;
    // Catálogo de "Organización" de Contratos (organismos/organizaciones), donde vive el
    // `idOrganizacion` del contrato. Se pide sin filtrar (a diferencia del combo de Contratos,
    // que excluye GRV) porque acá necesitamos poder resolver cualquier id, incluido GRV.
    const { data: contractOrganizationsList } = useGetOrganizationsQuery({});
    const contractOrganizationName = (contractOrganizationsList || []).find(
        (org: any) => Number(org?.id) === Number(employerContractData?.idOrganizacion)
    )?.nombre;
    const matchedArtEaOrganization = findOrganizacionArtEaMatch(
        contractOrganizationName,
        srtParametricData?.organizaciones
    );
    const [searchSiniestrosByAffectedDocument] = useLazyGetSiniestrosQuery();
    const srtFormSections = useMemo(() => buildSrtFormSections(t), [t]);
    const constructionSiteFields = useMemo(() => buildConstructionSiteFields(t), [t]);
    const otherAffectedDocumentLengths = useMemo(() => {
        const otherAffectedSection = srtFormSections.find((section) => section.id === 9);
        const otherAffectedField = otherAffectedSection?.fields.find(
            (field): field is SrtRepeatableConfig =>
                isRepeatableField(field) && field.name === 'otrosAccidentados'
        );
        const documentField = otherAffectedField?.fields.find((field) => field.name === 'cuil');

        return documentField?.exactLengthByFieldValue?.lengths || {};
    }, [srtFormSections]);
    const ciiuCodes = ciiuCodesResponse?.data?.codigos || [];
    const [values, setValues] = useState<SrtFormValues>(() => {
        const initialValues = buildInitialValues(siniestro);

        constructionSiteFields.forEach((field) => {
            initialValues[field.name] = '';
        });

        return applyArtEaContractDefaults(
            applyEmployerContractDefaults(
                applyEmployerDefaultsToWorkCenter(initialValues),
                employerContractData,
                employerCiiuRevision3Id
            ),
            matchedArtEaOrganization
        );
    });
    const [errors, setErrors] = useState<SrtFormErrors>({});

    useEffect(() => {
        if (!employerContractData) return;

        setValues((currentValues) =>
            applyEmployerContractDefaults(
                currentValues,
                employerContractData,
                employerCiiuRevision3Id
            )
        );
    }, [employerContractData, employerCiiuRevision3Id]);

    // `razonSocialEmpleador`/`cuitDatosEmpleador` se calculan una sola vez en buildInitialValues,
    // así que en el flujo de Visitas quedan vacíos si todavía no resolvió `roamSiniestroForEmployer`
    // al momento del montaje (ver arriba). Se completan acá cuando llega, sin pisar lo ya cargado.
    // Se pasa por `applyEmployerDefaultsToWorkCenter` para que, en ese mismo momento, arrastre
    // también Razón Social/CUIT del "Centro de Trabajo" (sección 04) — si no, esos dos quedarían
    // vacíos porque esa función solo corre al montar el form / en resetForm, antes de que este
    // efecto asíncrono resuelva.
    useEffect(() => {
        if (!employerOrganismoCentral) return;

        setValues((currentValues) =>
            applyEmployerDefaultsToWorkCenter({
                ...currentValues,
                razonSocialEmpleador: hasValue(currentValues.razonSocialEmpleador)
                    ? currentValues.razonSocialEmpleador
                    : employerOrganismoCentral?.nombre || '',
                cuitDatosEmpleador: hasValue(currentValues.cuitDatosEmpleador)
                    ? currentValues.cuitDatosEmpleador
                    : employerOrganismoCentral?.cuit || ''
            })
        );
    }, [employerOrganismoCentral]);

    useEffect(() => {
        if (!matchedArtEaOrganization) return;

        setValues((currentValues) =>
            applyArtEaContractDefaults(currentValues, matchedArtEaOrganization)
        );
    }, [matchedArtEaOrganization]);

    useEffect(() => {
        if (!srtParametricData) return;

        setValues((currentValues) => {
            const nextValues = { ...currentValues };
            let hasChanges = false;

            const codificationFallbacks: Array<{
                codeField:
                    | 'codigoZonaCuerpo'
                    | 'codigoNaturalezaLesion'
                    | 'codigoFormaAccidente'
                    | 'codigoAgenteCausante';
                descriptionField:
                    | 'zonaCuerpoDescripcion'
                    | 'naturalezaLesionDescripcion'
                    | 'formaAccidenteDescripcion'
                    | 'agenteCausanteDescripcion';
                sourceKey:
                    | 'zonasCuerpo'
                    | 'naturalezaLesiones'
                    | 'formasAccidentes'
                    | 'agentesCausantes';
            }> = [
                {
                    codeField: 'codigoZonaCuerpo',
                    descriptionField: 'zonaCuerpoDescripcion',
                    sourceKey: 'zonasCuerpo'
                },
                {
                    codeField: 'codigoNaturalezaLesion',
                    descriptionField: 'naturalezaLesionDescripcion',
                    sourceKey: 'naturalezaLesiones'
                },
                {
                    codeField: 'codigoFormaAccidente',
                    descriptionField: 'formaAccidenteDescripcion',
                    sourceKey: 'formasAccidentes'
                },
                {
                    codeField: 'codigoAgenteCausante',
                    descriptionField: 'agenteCausanteDescripcion',
                    sourceKey: 'agentesCausantes'
                }
            ];

            codificationFallbacks.forEach(({ codeField, descriptionField, sourceKey }) => {
                if (currentValues[codeField] && !currentValues[descriptionField]) {
                    const matchedByCode = (srtParametricData[sourceKey] || []).find(
                        (item: any) => String(item.id) === String(currentValues[codeField])
                    );

                    if (!matchedByCode) return;

                    nextValues[descriptionField] =
                        matchedByCode.descripcion || matchedByCode.nombre || '';
                    hasChanges = true;
                    return;
                }

                if (currentValues[codeField]) return;

                const matchedItem = findParametricItemByDescription(
                    srtParametricData[sourceKey],
                    currentValues[descriptionField]
                );

                if (!matchedItem) return;

                nextValues[codeField] = String(matchedItem.id);
                hasChanges = true;
            });

            if (
                currentValues.denominacionArtEa &&
                (!currentValues.codigoArtEa ||
                    !currentValues.denominacionArtCentro ||
                    !currentValues.codigoArtCentro)
            ) {
                const selectedOrganization = getSelectedParametricItem(
                    'denominacionArtEa',
                    currentValues.denominacionArtEa,
                    srtParametricData
                );

                if (selectedOrganization) {
                    if (!currentValues.codigoArtEa) {
                        nextValues.codigoArtEa = String(
                            selectedOrganization.codigoAseguradora ||
                                selectedOrganization.codigo ||
                                ''
                        );
                        hasChanges = true;
                    }
                    if (!currentValues.denominacionArtCentro) {
                        nextValues.denominacionArtCentro =
                            selectedOrganization.descripcion || selectedOrganization.nombre || '';
                        hasChanges = true;
                    }
                    if (!currentValues.codigoArtCentro) {
                        nextValues.codigoArtCentro = String(
                            selectedOrganization.codigoAseguradora ||
                                selectedOrganization.codigo ||
                                ''
                        );
                        hasChanges = true;
                    }
                }
            }

            return hasChanges ? nextValues : currentValues;
        });
    }, [srtParametricData]);

    const resetForm = useCallback(
        (nextValues?: Partial<SrtFormValues>) => {
            const initialValues = buildInitialValues(siniestro);

            constructionSiteFields.forEach((field) => {
                initialValues[field.name] = '';
            });

            setValues(
                syncInterviewDates(
                    applyArtEaContractDefaults(
                        applyEmployerContractDefaults(
                            applyEmployerDefaultsToWorkCenter({
                                ...initialValues,
                                ...(nextValues || {}),
                                medidasCorrectivas: normalizeMedidasCorrectivasRows(
                                    ((nextValues || {}).medidasCorrectivas as
                                        | Record<string, any>[]
                                        | undefined) ||
                                        (initialValues.medidasCorrectivas as
                                            | Record<string, any>[]
                                            | undefined)
                                )
                            }),
                            employerContractData,
                            employerCiiuRevision3Id
                        ),
                        matchedArtEaOrganization
                    )
                )
            );
            setErrors({});
        },
        [siniestro, employerContractData, employerCiiuRevision3Id, matchedArtEaOrganization]
    );

    const handleFieldChange = useCallback(
        (name: string, value: any) => {
            setValues((currentValues) => {
                const nextValue = limitResponsibleNamePart(name, value, currentValues);
                const nextValues = updateEmployerFieldWithWorkCenterDefault(
                    currentValues,
                    name,
                    nextValue
                );
                const selectedItem = getSelectedParametricItem(name, value, srtParametricData);

                if (name === 'codigoZonaCuerpo' && selectedItem) {
                    nextValues.zonaCuerpoDescripcion = selectedItem.descripcion;
                }

                if (name === 'codigoNaturalezaLesion' && selectedItem) {
                    nextValues.naturalezaLesionDescripcion = selectedItem.descripcion;
                }

                if (name === 'codigoFormaAccidente' && selectedItem) {
                    nextValues.formaAccidenteDescripcion = selectedItem.descripcion;
                }

                if (name === 'codigoAgenteCausante' && selectedItem) {
                    nextValues.agenteCausanteDescripcion = selectedItem.descripcion;
                }

                if (name === 'denominacionArtEa' && selectedItem) {
                    nextValues.codigoArtEa = String(
                        selectedItem.codigoAseguradora || selectedItem.codigo || ''
                    );
                    nextValues.denominacionArtCentro =
                        selectedItem.descripcion || selectedItem.nombre || '';
                    nextValues.codigoArtCentro = String(
                        selectedItem.codigoAseguradora || selectedItem.codigo || ''
                    );
                }

                return name === 'responsableFecha' ? syncInterviewDates(nextValues) : nextValues;
            });
            setErrors((currentErrors) => ({
                ...currentErrors,
                [name]: '',
                ...(RESPONSIBLE_NAME_FIELDS.has(name)
                    ? { responsableApellido: '', responsableNombres: '' }
                    : {})
            }));
        },
        [srtParametricData]
    );

    const handleRepeatableFieldChange = useCallback(
        (groupName: string, rowIndex: number, fieldName: string, value: any) => {
            if (
                (groupName === 'medidasCorrectivas' && fieldName === 'nroCausa') ||
                (groupName === 'entrevistados' && fieldName === 'fecha')
            ) {
                return;
            }

            setValues((currentValues) => {
                const rows = [...(currentValues[groupName] || [])];
                rows[rowIndex] = {
                    ...rows[rowIndex],
                    [fieldName]: value
                };

                if (
                    (groupName === 'entrevistados' || groupName === 'testigos') &&
                    fieldName === 'tipoDocumento'
                ) {
                    rows[rowIndex] = {
                        ...rows[rowIndex],
                        dniCuil: ''
                    };
                }

                if (groupName === 'otrosAccidentados' && fieldName === 'tipoDocumento') {
                    rows[rowIndex] = {
                        ...rows[rowIndex],
                        cuil: ''
                    };
                }

                if (
                    groupName === 'medidasCorrectivas' &&
                    fieldName === 'requiereMedidaCorrectiva' &&
                    value === 'no'
                ) {
                    rows[rowIndex] = {
                        ...rows[rowIndex],
                        medidas: [createEmptyMedida()]
                    };
                }

                return {
                    ...currentValues,
                    [groupName]:
                        groupName === 'medidasCorrectivas'
                            ? normalizeMedidasCorrectivasRows(rows)
                            : rows
                };
            });
            setErrors((currentErrors) => ({
                ...currentErrors,
                [getErrorKey(groupName, rowIndex, fieldName)]: '',
                ...(groupName === 'entrevistados' && fieldName === 'tipoDocumento'
                    ? { [getErrorKey(groupName, rowIndex, 'dniCuil')]: '' }
                    : {}),
                ...(groupName === 'otrosAccidentados' && fieldName === 'tipoDocumento'
                    ? { [getErrorKey(groupName, rowIndex, 'cuil')]: '' }
                    : {})
            }));
        },
        []
    );

    const addRepeatableRow = useCallback((field: SrtRepeatableConfig) => {
        setValues((currentValues) => {
            const currentRows = currentValues[field.name] || [];
            if (currentRows.length >= MAX_REPEATABLE_ROWS) {
                return currentValues;
            }

            const nextRows = [...currentRows, createEmptyRepeatableRow(field)];

            const nextValues = {
                ...currentValues,
                [field.name]:
                    field.name === 'medidasCorrectivas'
                        ? normalizeMedidasCorrectivasRows(nextRows)
                        : nextRows
            };

            return field.name === 'entrevistados' ? syncInterviewDates(nextValues) : nextValues;
        });
    }, []);

    const removeRepeatableRow = useCallback((groupName: string, rowIndex: number) => {
        setValues((currentValues) => {
            const rows = [...(currentValues[groupName] || [])];
            if (rows.length <= 1) {
                const firstRow = rows[0] || {};
                const clearedRow = Object.keys(firstRow).reduce<Record<string, any>>((acc, key) => {
                    acc[key] = null;
                    if (typeof firstRow[key] === 'string') {
                        acc[key] = '';
                    }
                    if (firstRow[key] instanceof File) {
                        acc[key] = null;
                    }
                    return acc;
                }, {});

                const nextValues = {
                    ...currentValues,
                    [groupName]:
                        groupName === 'medidasCorrectivas'
                            ? normalizeMedidasCorrectivasRows([clearedRow])
                            : [clearedRow]
                };

                return groupName === 'entrevistados' ? syncInterviewDates(nextValues) : nextValues;
            }
            rows.splice(rowIndex, 1);

            const nextValues = {
                ...currentValues,
                [groupName]:
                    groupName === 'medidasCorrectivas'
                        ? normalizeMedidasCorrectivasRows(rows)
                        : rows
            };

            return groupName === 'entrevistados' ? syncInterviewDates(nextValues) : nextValues;
        });
    }, []);

    const handleMedidaFieldChange = useCallback(
        (causaIndex: number, medidaIndex: number, fieldName: string, value: any) => {
            setValues((currentValues) => {
                const causas = [...(currentValues.medidasCorrectivas || [])];
                if (!causas[causaIndex]) return currentValues;

                const medidas = [...(causas[causaIndex].medidas || [])];
                medidas[medidaIndex] = { ...medidas[medidaIndex], [fieldName]: value };
                causas[causaIndex] = { ...causas[causaIndex], medidas };

                return {
                    ...currentValues,
                    medidasCorrectivas: normalizeMedidasCorrectivasRows(causas)
                };
            });
            setErrors((currentErrors) => ({
                ...currentErrors,
                [`medidasCorrectivas.${causaIndex}.medidas.${medidaIndex}.${fieldName}`]: ''
            }));
        },
        []
    );

    const addMedidaRow = useCallback((causaIndex: number) => {
        setValues((currentValues) => {
            const causas = [...(currentValues.medidasCorrectivas || [])];
            const causa = causas[causaIndex];
            if (!causa) return currentValues;

            const medidas = causa.medidas || [];
            if (medidas.length >= MAX_REPEATABLE_ROWS) return currentValues;

            causas[causaIndex] = { ...causa, medidas: [...medidas, createEmptyMedida()] };

            return {
                ...currentValues,
                medidasCorrectivas: normalizeMedidasCorrectivasRows(causas)
            };
        });
    }, []);

    const removeMedidaRow = useCallback((causaIndex: number, medidaIndex: number) => {
        setValues((currentValues) => {
            const causas = [...(currentValues.medidasCorrectivas || [])];
            const causa = causas[causaIndex];
            if (!causa) return currentValues;

            const medidas = [...(causa.medidas || [])];

            if (medidas.length <= 1) {
                medidas[0] = createEmptyMedida();
            } else {
                medidas.splice(medidaIndex, 1);
            }

            causas[causaIndex] = { ...causa, medidas };

            return {
                ...currentValues,
                medidasCorrectivas: normalizeMedidasCorrectivasRows(causas)
            };
        });
    }, []);

    const [otherAffectedLookupLoading, setOtherAffectedLookupLoading] = useState<
        Record<number, boolean>
    >({});
    const otherAffectedLookupKeysRef = useRef<Record<number, string>>({});

    const lookupOtherAffectedDocument = useCallback(
        (rowIndex: number, tipoDocumento: string, documentValue: string) => {
            if (!tipoDocumento || !documentValue) return;

            setOtherAffectedLookupLoading((current) => ({ ...current, [rowIndex]: true }));

            searchSiniestrosByAffectedDocument(
                tipoDocumento === 'dni' ? { documento: documentValue } : { cuil: documentValue }
            )
                .unwrap()
                .then((result) => {
                    const { nroDenuncia, patologiaTrazadora, apellidoNombres } =
                        resolveOtherAffectedAutoFill(result?.data);

                    setValues((currentValues) => {
                        const rows = [...(currentValues.otrosAccidentados || [])];
                        const currentRow = rows[rowIndex];
                        const isStale =
                            !currentRow ||
                            currentRow.tipoDocumento !== tipoDocumento ||
                            String(currentRow.cuil || '') !== documentValue;

                        if (isStale) return currentValues;

                        rows[rowIndex] = {
                            ...currentRow,
                            nroDenuncia,
                            patologiaTrazadora,
                            ...(apellidoNombres ? { apellidoNombres } : {}),
                            ...(patologiaTrazadora ? { tipoLesion: '', descripcionLesion: '' } : {})
                        };

                        return { ...currentValues, otrosAccidentados: rows };
                    });

                    if (patologiaTrazadora) {
                        setErrors((currentErrors) => ({
                            ...currentErrors,
                            [getErrorKey('otrosAccidentados', rowIndex, 'tipoLesion')]: '',
                            [getErrorKey('otrosAccidentados', rowIndex, 'descripcionLesion')]: ''
                        }));
                    }
                })
                .catch(() => {
                    // Búsqueda best-effort: si falla, el preventor completa nroDenuncia/patologiaTrazadora manualmente.
                })
                .finally(() => {
                    setOtherAffectedLookupLoading((current) => {
                        if (!current[rowIndex]) return current;

                        const { [rowIndex]: _removedLoading, ...rest } = current;
                        return rest;
                    });
                });
        },
        [searchSiniestrosByAffectedDocument]
    );

    useEffect(() => {
        const rows = values.otrosAccidentados || [];

        rows.forEach((row: Record<string, any>, rowIndex: number) => {
            const tipoDocumento = row?.tipoDocumento;
            const documentValue = String(row?.cuil || '');
            const expectedLength = otherAffectedDocumentLengths[tipoDocumento];

            if (!tipoDocumento || !expectedLength || documentValue.length !== expectedLength) {
                delete otherAffectedLookupKeysRef.current[rowIndex];
                return;
            }

            const lookupKey = `${tipoDocumento}:${documentValue}`;

            if (otherAffectedLookupKeysRef.current[rowIndex] === lookupKey) return;

            otherAffectedLookupKeysRef.current[rowIndex] = lookupKey;
            lookupOtherAffectedDocument(rowIndex, tipoDocumento, documentValue);
        });
    }, [values.otrosAccidentados, otherAffectedDocumentLengths, lookupOtherAffectedDocument]);

    const validateField = useCallback(
        (field: SrtBaseFieldConfig, value: any, groupName?: string, rowIndex?: number) => {
            // Medidas dentro de una causa (groupName compuesto "medidasCorrectivas.<causaIndex>.medidas",
            // ver onNestedBlur en index.tsx): nroMedida es editable a mano, pero debe ser un
            // numero correlativo dentro de una unica secuencia GLOBAL a lo largo de TODAS las
            // causas (1..maxOtraMedida+1), no solo dentro de esa causa: si la causa 1 ya tiene
            // las medidas 1 y 2, la primera medida de la causa 2 tiene que poder ser la 3.
            const medidaGroupMatch = groupName?.match(/^medidasCorrectivas\.(\d+)\.medidas$/);

            if (medidaGroupMatch && groupName && field.name === 'nroMedida') {
                const causaIndex = Number(medidaGroupMatch[1]);
                const errorKey = getErrorKey(groupName, rowIndex, field.name);
                let errorMsg = '';

                if (value === '' || value === null || value === undefined) {
                    errorMsg = tValidation('validation.required');
                } else {
                    const causas: Record<string, any>[] = values.medidasCorrectivas || [];
                    const otherNumbers = causas
                        .flatMap((causa, cIndex) =>
                            causa?.requiereMedidaCorrectiva === 'si'
                                ? (causa.medidas || []).map(
                                      (medida: Record<string, any>, mIndex: number) => ({
                                          nroMedida: medida?.nroMedida,
                                          isCurrent: cIndex === causaIndex && mIndex === rowIndex
                                      })
                                  )
                                : []
                        )
                        .filter((entry) => !entry.isCurrent)
                        .map((entry) => Number(entry.nroMedida))
                        .filter((n) => Number.isFinite(n) && n > 0);
                    const maxN = otherNumbers.length > 0 ? Math.max(...otherNumbers) : 0;
                    const inputNum = Number(value);

                    if (inputNum < 1 || inputNum > maxN + 1) {
                        errorMsg = t('detail.srtForm.alerts.nroMedidaInvalid', {
                            next: maxN + 1
                        });
                    } else {
                        // Ademas del rango global, dentro de UNA MISMA causa no puede haber dos
                        // medidas con el mismo numero (aunque ese numero sea valido a nivel
                        // global, ej. porque coincide con el de una medida de otra causa).
                        const causaMedidas: Record<string, any>[] =
                            causas[causaIndex]?.medidas || [];
                        const isDuplicateWithinCausa = causaMedidas.some(
                            (medida, mIndex) =>
                                mIndex !== rowIndex && Number(medida?.nroMedida) === inputNum
                        );

                        if (isDuplicateWithinCausa) {
                            errorMsg = t('detail.srtForm.alerts.nroMedidaDuplicado');
                        }
                    }
                }

                setErrors((ce) => ({ ...ce, [errorKey]: errorMsg }));
                return !errorMsg;
            }

            const isResponsibleNameField = !groupName && RESPONSIBLE_NAME_FIELDS.has(field.name);
            const responsibleFullNameLength = isResponsibleNameField
                ? getResponsibleFullNameLength({
                      ...values,
                      [field.name]: value
                  })
                : 0;
            const responsibleNameError =
                responsibleFullNameLength > MAX_FULL_NAME_LENGTH
                    ? tValidation('validation.maxLength', { param: MAX_FULL_NAME_LENGTH })
                    : '';
            const { hasError, errorText } = responsibleNameError
                ? { hasError: true, errorText: responsibleNameError }
                : validate(value, field.validations || []);
            const errorKey = getErrorKey(groupName || field.name, rowIndex, field.name);

            setErrors((currentErrors) => ({
                ...currentErrors,
                [errorKey]: hasError ? errorText : ''
            }));

            return !hasError;
        },
        [t, tValidation, validate, values]
    );

    const findFieldByName = useCallback(
        (fieldName: string): SrtBaseFieldConfig | null => {
            const findInFields = (fields: SrtFieldConfig[]): SrtBaseFieldConfig | null => {
                for (const field of fields) {
                    if (isDividerField(field)) continue;

                    if (isRepeatableField(field)) {
                        const nestedField = findInFields(field.fields);
                        if (nestedField) return nestedField;

                        if (field.nestedRepeatable) {
                            const deeplyNestedField = findInFields(field.nestedRepeatable.fields);
                            if (deeplyNestedField) return deeplyNestedField;
                        }
                        continue;
                    }

                    if (field.name === fieldName) {
                        return field;
                    }
                }

                return null;
            };

            for (const section of srtFormSections) {
                const field = findInFields(section.fields);
                if (field) return field;
            }

            return null;
        },
        [srtFormSections]
    );

    useEffect(() => {
        const nroDenunciaField = findFieldByName('nroDenuncia');
        if (nroDenunciaField) {
            validateField(nroDenunciaField, values.nroDenuncia);
        }
    }, [findFieldByName, validateField, values.nroDenuncia]);

    useEffect(() => {
        const placeCPAField = findFieldByName('lugarCPA');
        if (placeCPAField) {
            validateField(placeCPAField, values.lugarCPA);
        }
    }, [findFieldByName, validateField, values.lugarCPA]);

    useEffect(() => {
        const responsableApellidoField = findFieldByName('responsableApellido');
        const responsableNombresField = findFieldByName('responsableNombres');

        if (responsableApellidoField) {
            validateField(responsableApellidoField, values.responsableApellido);
        }

        if (responsableNombresField) {
            validateField(responsableNombresField, values.responsableNombres);
        }
    }, [findFieldByName, validateField, values.responsableApellido, values.responsableNombres]);

    const constructionSiteFieldsWithOptions = useMemo(
        () =>
            constructionSiteFields.map((field) =>
                mapBaseFieldOptions(field, srtParametricData, ciiuCodes)
            ),
        [ciiuCodes, srtParametricData]
    );

    const isConstructionSiteSelected = String(values.tipoLugar ?? '') === CONSTRUCTION_SITE_VALUE;

    const sectionsStatus = useMemo(() => {
        const status = srtFormSections.reduce<Record<number, boolean>>((acc, section) => {
            acc[section.id] = isSectionComplete(section, values);
            return acc;
        }, {});

        if (isConstructionSiteSelected) {
            status[4] =
                status[4] &&
                constructionSiteFieldsWithOptions.every((field) => hasValue(values[field.name]));
        }

        return status;
    }, [constructionSiteFieldsWithOptions, isConstructionSiteSelected, values]);

    const sectionsWithContent = useMemo(
        () =>
            srtFormSections.reduce<Record<number, boolean>>((acc, section) => {
                acc[section.id] = hasSectionContent(section, values);
                return acc;
            }, {}),
        [srtFormSections, values]
    );

    const sections = useMemo<SrtSectionConfig[]>(() => {
        const causeOptions = (values.medidasCorrectivas || [])
            .map((row: Record<string, any>) => row?.nroCausa)
            .filter(Boolean)
            .map((nroCausa: string) => ({
                label: nroCausa,
                value: nroCausa
            }));

        return srtFormSections.map(
            (section): SrtSectionConfig => ({
                ...section,
                fields: section.fields.map((field): SrtFieldConfig => {
                    const mappedField = mapFieldOptions(field, srtParametricData);
                    const mappedFieldWithCiiu = mapFieldOptions(
                        mappedField,
                        srtParametricData,
                        ciiuCodes
                    );
                    if (
                        !isRepeatableField(mappedFieldWithCiiu) &&
                        !isDividerField(mappedFieldWithCiiu) &&
                        RESPONSIBLE_NAME_FIELDS.has(mappedFieldWithCiiu.name)
                    ) {
                        const otherFieldName =
                            mappedFieldWithCiiu.name === 'responsableApellido'
                                ? 'responsableNombres'
                                : 'responsableApellido';
                        const otherFieldLength = String(values[otherFieldName] || '').length;

                        return {
                            ...mappedFieldWithCiiu,
                            maxLength: Math.max(MAX_FULL_NAME_LENGTH - otherFieldLength, 0),
                            showCharacterCounter: mappedFieldWithCiiu.name === 'responsableNombres',
                            characterCounterMax: MAX_FULL_NAME_LENGTH,
                            characterCount:
                                mappedFieldWithCiiu.name === 'responsableNombres'
                                    ? getResponsibleFullNameLength(values)
                                    : undefined
                        };
                    }

                    if (
                        !isRepeatableField(mappedFieldWithCiiu) ||
                        mappedFieldWithCiiu.name !== 'seguimientos'
                    ) {
                        return mappedFieldWithCiiu;
                    }

                    return {
                        ...mappedFieldWithCiiu,
                        fields: mappedFieldWithCiiu.fields.map(
                            (childField): SrtBaseFieldConfig =>
                                childField.name === 'medida'
                                    ? { ...childField, options: causeOptions }
                                    : childField
                        )
                    } satisfies SrtRepeatableConfig;
                })
            })
        );
    }, [ciiuCodes, srtParametricData, values.medidasCorrectivas]);

    return {
        constructionSiteFields: constructionSiteFieldsWithOptions,
        isConstructionSiteSelected,
        srtParametricData,
        employerContractData,
        sections,
        values,
        errors,
        sectionsStatus,
        sectionsWithContent,
        handleFieldChange,
        resetForm,
        setValues,
        handleRepeatableFieldChange,
        addRepeatableRow,
        removeRepeatableRow,
        handleMedidaFieldChange,
        addMedidaRow,
        removeMedidaRow,
        validateField,
        otherAffectedLookupLoading,
        maxRepeatableRows: MAX_REPEATABLE_ROWS,
        validateSection: (sectionId: number) => {
            const section = sections.find((currentSection) => currentSection.id === sectionId);
            if (!section) return true;

            let isSectionValid = true;

            section.fields.forEach((field) => {
                if (isDividerField(field)) return;
                if (!shouldCountFieldForSectionCompletion(section.id, field, values)) return;

                if (isRepeatableField(field)) {
                    (values[field.name] || []).forEach(
                        (row: Record<string, any>, rowIndex: number) => {
                            field.fields.forEach((childField) => {
                                if (!isSrtFieldVisible(childField, row)) return;

                                const resolvedField = resolveSrtField(childField, row);

                                const isFieldValid = validateField(
                                    resolvedField,
                                    row?.[resolvedField.name],
                                    field.name,
                                    rowIndex
                                );

                                if (!isFieldValid) {
                                    isSectionValid = false;
                                }
                            });

                            const nested = field.nestedRepeatable;
                            if (
                                nested &&
                                (!nested.visibleWhen ||
                                    String(row?.[nested.visibleWhen.fieldName] ?? '') ===
                                        String(nested.visibleWhen.value))
                            ) {
                                (row?.[nested.name] || []).forEach(
                                    (nestedRow: Record<string, any>, nestedIndex: number) => {
                                        nested.fields.forEach((childField) => {
                                            if (!isSrtFieldVisible(childField, nestedRow)) return;

                                            const resolvedField = resolveSrtField(
                                                childField,
                                                nestedRow
                                            );

                                            const isFieldValid = validateField(
                                                resolvedField,
                                                nestedRow?.[resolvedField.name],
                                                `${field.name}.${rowIndex}.${nested.name}`,
                                                nestedIndex
                                            );

                                            if (!isFieldValid) {
                                                isSectionValid = false;
                                            }
                                        });
                                    }
                                );
                            }
                        }
                    );
                    return;
                }

                const isFieldValid = validateField(field, values[field.name]);

                if (!isFieldValid) {
                    isSectionValid = false;
                }
            });

            return isSectionValid;
        }
    };
};
